import { z } from 'zod';
import { BODY_FORMATS } from '../lib/body';
import { INQUIRY_STATUSES } from '../lib/constants';

/** One message in a batch. The planner has seen and possibly edited each of
 *  these individually, so subject and body arrive fully rendered — the server
 *  re-validates for unresolved merge variables but does not re-merge. */
/**
 * How long a body may be, by format.
 *
 * The plain-text ceilings are the ones that were here before rich bodies
 * existed and are deliberately unchanged: a 25k plain-text reply was too long
 * to be a reply then and still is. An html body of the same prose is several
 * times the bytes — every paragraph carries its own tags — so it gets its own
 * ceiling rather than forcing the text one up to meet it. Images cost nothing
 * against either: they are stored bytes referenced by a short url.
 */
export const BODY_MAX_CHARS = {
  composed: { text: 50_000, html: 200_000 },
  reply: { text: 25_000, html: 120_000 },
} as const;

export const composedMessageSchema = z
  .object({
    vendorId: z.string().uuid(),
    subject: z.string().min(1).max(255),
    body: z.string().min(1).max(BODY_MAX_CHARS.composed.html),
    /** How to read `body`. An HTML body is re-sanitized server-side before it
     *  is queued — the editor's schema constrains what a planner can build,
     *  but this endpoint accepts whatever a client sends. */
    bodyFormat: z.enum(BODY_FORMATS).default('text'),
  })
  .superRefine((value, ctx) => {
    const limit = BODY_MAX_CHARS.composed[value.bodyFormat];
    if (value.body.length > limit) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: limit,
        type: 'string',
        inclusive: true,
        path: ['body'],
        message: 'That message is too long to send.',
      });
    }
  });

export const createBatchSchema = z.object({
  weddingId: z.string().uuid(),
  category: z.string().min(1).max(100),
  messages: z.array(composedMessageSchema).min(1).max(25),
});

export const updateInquiryStatusSchema = z.object({
  status: z.enum(INQUIRY_STATUSES),
});

export const updateInquirySchema = z.object({
  status: z.enum(INQUIRY_STATUSES).optional(),
  /**
   * A number is the planner's own figure and outranks every extraction from
   * then on. `null` is not "store nothing" — it is a *rejection*: the server
   * stamps `rejectedAt` on whichever row produced the figure that was on
   * screen, so the next resolve cannot hand the same number straight back.
   * See `rejectResolved` in `backend/src/lib/quote-headline.ts`.
   */
  quoteAmount: z.number().min(0).nullable().optional(),
  quoteCurrency: z.string().length(3).optional(),
  /**
   * Which package of a multi-tier quote the planner is actually considering.
   * Moves `isSelected` within that quote — exactly one package carries it —
   * and the headline follows, because resolution reads `selected ?? first`.
   *
   * Scoped to the quote it belongs to and validated against this inquiry's
   * own quotes server-side: a package id is not a capability.
   */
  selectedPackageId: z.string().uuid().optional(),
  /**
   * Undo of a rejection: put back the reading the planner just struck out.
   *
   * It names the row rather than saying "the last one" because a rejection
   * leaves no trace on screen once the toast carrying its Undo has gone, and
   * two rejections in a row are an ordinary sequence — the two-step clear
   * (drop your override, then reject the vendor's figure) can be followed by
   * another on the figure beneath. A boolean would make the wrong row the
   * silent outcome; naming the row makes it a 409 the planner can read.
   *
   * Which id, is which kind of row was stamped, and the resolution the planner
   * was looking at says so: `quote.quoteId` when the headline came from a
   * package tree, `quote.sourceMessageId` when it came from a bare
   * `messageQuotes` reading. The server refuses anything that is not the most
   * recent rejection — see `clearRejection` — so a stale toast cannot quietly
   * undo something else.
   *
   * `.strict()` on both branches so a body carrying both ids is a 400 rather
   * than one of them being silently ignored.
   */
  unrejectQuote: z
    .union([
      z.object({ quoteId: z.string().uuid() }).strict(),
      z.object({ messageId: z.string().uuid() }).strict(),
    ])
    .optional(),
});

/** A quick reply typed in the app. See `BODY_MAX_CHARS` for why the ceiling
 *  depends on the format rather than being one number for both. */
export const replyToInquirySchema = z
  .object({
    text: z.string().trim().min(1).max(BODY_MAX_CHARS.reply.html),
    /** How to read `text`. Older clients omit it and keep sending plain text. */
    bodyFormat: z.enum(BODY_FORMATS).default('text'),
    /** Which of the inquiry's Gmail threads to answer. Omitted by older
     *  clients, which get the inquiry's original thread. */
    gmailThreadId: z.string().min(1).max(64).optional(),
  })
  .superRefine((value, ctx) => {
    const limit = BODY_MAX_CHARS.reply[value.bodyFormat];
    if (value.text.length > limit) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: limit,
        type: 'string',
        inclusive: true,
        path: ['text'],
        message: 'That reply is too long to send.',
      });
    }
  });

export const bulkUpdateStatusSchema = z.object({
  inquiryIds: z.array(z.string().uuid()).min(1).max(200),
  status: z.enum(INQUIRY_STATUSES),
});

export type ComposedMessage = z.infer<typeof composedMessageSchema>;
export type CreateBatchInput = z.infer<typeof createBatchSchema>;
export type UpdateInquiryInput = z.infer<typeof updateInquirySchema>;
export type BulkUpdateStatusInput = z.infer<typeof bulkUpdateStatusSchema>;
export type ReplyToInquiryInput = z.infer<typeof replyToInquirySchema>;
